import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";

/*
  POPIA-aligned cookie consent banner.
  Consent choice + timestamp is stored in localStorage for audit purposes.
  v2 schema:
    {
      choice: "accept" | "reject",
      timestamp: <ISO date-time>,
      policy_version: "2026-04"
    }
*/
const STORAGE_KEY = "rcs-cookie-consent-v2";
const POLICY_VERSION = "2026-07";

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    // Show the banner if there's no stored choice OR the stored policy version doesn't match.
    if (!existing || existing.policy_version !== POLICY_VERSION) setVisible(true);
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          choice,
          timestamp: new Date().toISOString(),
          policy_version: POLICY_VERSION,
        }),
      );
    } catch {
      // localStorage blocked — treat as implicit reject; do nothing.
    }
    // Google Consent Mode v2 — update analytics/ads storage to match the choice.
    if (typeof window.gtag === "function") {
      const state = choice === "accept" ? "granted" : "denied";
      window.gtag("consent", "update", {
        ad_storage: state,
        ad_user_data: state,
        ad_personalization: state,
        analytics_storage: state,
      });
    }
    // Notify listeners (e.g. Microsoft Clarity loader) that consent changed.
    try { window.dispatchEvent(new Event("rcs-consent-updated")); } catch (e) {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50 animate-rise"
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="bg-[#0B5FFF] text-white rounded-lg shadow-2xl border border-white/10 p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold text-sm flex items-center gap-2">
              We value your privacy
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" aria-label="POPIA-aligned" />
            </div>
            <p className="text-xs text-white/80 mt-1 leading-relaxed">
              RapidCare SA uses essential cookies to keep you signed in, and optional preference cookies to remember your choices. We also use <strong>Google Analytics</strong> and <strong>Google Ads</strong> (via Google Tag Manager) to understand how the platform is used and to measure our advertising. Cookies never store clinical or patient data. Our payment provider (Payfast) sets its own cookies during checkout.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                onClick={() => persist("accept")}
                size="sm"
                className="bg-white text-[#111827] hover:bg-slate-100 h-8 text-xs px-4"
                data-testid="cookie-accept"
              >
                Accept cookies
              </Button>
              <Button
                onClick={() => persist("reject")}
                size="sm"
                variant="outline"
                className="border-white/20 bg-transparent text-white hover:bg-white/10 h-8 text-xs px-4"
                data-testid="cookie-reject"
              >
                Reject non-essential
              </Button>
              <Link to="/cookie-policy">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 bg-transparent text-white hover:bg-white/10 h-8 text-xs px-4"
                  data-testid="cookie-learn-more"
                >
                  View cookie policy
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
